import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Modal, Row, Typography } from 'antd';
import { Alert, Box } from '@mui/material';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import WebGrid from '../../webgrid/src';
import { DocumentScanModal } from '../components/DocumentScanModal';
import { IdCardReaderModal } from '../components/IdCardReaderModal';
import { IdentityReaderLauncher } from '../components/IdentityReaderLauncher';
import { useAuth } from '../contexts/AuthContext';
import useQuery from '../hooks/useQuery';
import useRequest from '../hooks/useRequest';
import {
    mapIdCardToClientForm,
    type ClientFormValues,
    type IdCardReadResult,
} from '../domain/idCardClientForm';
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';
import {
    buildScanAuditFields,
    chipProvenance,
    describeFailure,
    type ScanProvenance,
} from '../domain/documentScanAudit';

const { Title, Text } = Typography;

interface ClientData {
    ClNr: number;
    Nume: string;
    Telefon?: string;
    Mobil?: string;
    Email?: string;
    CNP?: string;
    Oras?: string;
    Str?: string;
    Nr?: string;
    Ap?: string;
    Judet?: string;
    CodPostal?: string;
    photoUrl?: string;
    TaxCode?: string;
    RegistrationNumber?: string;
    Delegate?: string;
    SerieCI?: string;
    NrCI?: string;
    EmitentCI?: string;
    DataEliberareCI?: string;
    DataExpirareCI?: string;
    Notite?: string;
}

/** A portrait waiting for the client to be saved, as whichever reader produced it. */
type PendingPortrait =
    | { source: 'chip'; base64: string }
    | { source: 'optical'; blob: Blob }
    | null;

/**
 * Reads image bytes as base64, without the data-URL prefix.
 *
 * Deliberately not held anywhere: the caller uses the result for one request and lets it go. The
 * upload contract takes base64, so the encoding has to happen somewhere - this is the latest
 * possible place, which is the point.
 */
function toBase64(blob: Blob): Promise<string | null> {
    return new Promise(resolve => {
        const reader = new FileReader();

        reader.onerror = () => resolve(null);
        reader.onload = () => {
            const encoded = typeof reader.result === 'string' ? reader.result : '';
            const comma = encoded.indexOf(',');

            resolve(comma === -1 ? null : encoded.slice(comma + 1));
        };
        reader.readAsDataURL(blob);
    });
}

const Clients = () => {
    const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingClient, setEditingClient] = useState<ClientData | null>(null);
    const [idCardModalVisible, setIdCardModalVisible] = useState(false);
    /** The reader an optical scan is running on. Non-null exactly while that modal is open. */
    const [scanningDeviceId, setScanningDeviceId] = useState<string | null>(null);
    const [isCompany, setIsCompany] = useState(false);
    /**
     * The portrait to store once the client has an id.
     *
     * Two shapes because the two readers hand it over differently: the chip service answers with
     * base64 in its JSON, while the optical service answers with bytes. The optical one is kept as
     * a `Blob` rather than converted on arrival, so a base64 copy of somebody's face exists only
     * for the moment of the upload and is released immediately afterwards.
     */
    const [pendingPortrait, setPendingPortrait] = useState<PendingPortrait>(null);
    /**
     * Where the values in the open form came from, when they came off a document.
     *
     * Held on the page rather than in either modal because it has to outlive both: the read
     * finishes, the modal closes, and the audit is only truthful once the save it produced has
     * succeeded. Null means the operator typed it.
     */
    const [scanProvenance, setScanProvenance] = useState<ScanProvenance | null>(null);
    /** The workstation the launcher resolved, handed to the scan modal for its audit block. */
    const [opticalInstallationId, setOpticalInstallationId] = useState<string | null>(null);
    const [alertMessage, setAlertMessage] = useState<{
        type: 'success' | 'error' | 'warning';
        text: string;
    } | null>(null);
    const [form] = Form.useForm();
    const { user } = useAuth();
    const { t } = useTranslation();
    const request = useRequest();
    const [searchParams] = useSearchParams();

    // Check if user is admin
    const isAdmin =
        user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'administrator';

    // Portraits are personal data behind authentication, so the bytes are fetched with the
    // session's token and rendered from an in-memory object URL that is revoked on change.
    const { objectUrl: selectedClientPhotoUrl, failed: photoFailed } = useAuthenticatedImage(
        selectedClient?.photoUrl,
    );

    // Fetch clients data using useQuery hook
    const {
        data: clientsData,
        loading,
        error: clientsError,
        reload: reloadClients,
    } = useQuery<{ clients: ClientData[] }>({
        path: '/clients?limit=100000',
        method: 'GET',
    });

    const clients = useMemo(() => clientsData?.clients ?? [], [clientsData?.clients]);

    // Handle clients loading errors
    useEffect(() => {
        if (clientsError) {
            setAlertMessage({ type: 'error', text: 'Failed to load clients' });
            console.error('Load clients error:', clientsError);
        }
    }, [clientsError]);

    // Set company fields after the form fields are rendered (when isCompany becomes true)
    useEffect(() => {
        if (isCompany && editingClient && modalVisible) {
            form.setFieldsValue({
                taxCode: editingClient.TaxCode,
                registrationNumber: editingClient.RegistrationNumber,
                delegate: editingClient.Delegate,
            });
        }
    }, [isCompany, editingClient, modalVisible, form]);

    const handleAddClient = () => {
        setEditingClient(null);
        form.resetFields();
        setIsCompany(false);
        setModalVisible(true);
    };

    const handleEditClient = (client: ClientData) => {
        setEditingClient(client);
        const hasCompanyData = [
            client.TaxCode?.trim(),
            client.RegistrationNumber?.trim(),
            client.Delegate?.trim(),
        ].some(Boolean);
        setIsCompany(hasCompanyData);
        // Set non-company fields immediately
        form.setFieldsValue({
            nume: client.Nume,
            telefon: client.Telefon,
            mobil: client.Mobil,
            email: client.Email,
            cnp: client.CNP,
            oras: client.Oras,
            str: client.Str,
            nr: client.Nr,
            ap: client.Ap,
            jud: client.Judet,
            codPostal: client.CodPostal,
            serieCI: client.SerieCI,
            nrCI: client.NrCI,
            emitentCI: client.EmitentCI,
            dataEliberareCI: client.DataEliberareCI,
            dataExpirareCI: client.DataExpirareCI,
            notite: client.Notite,
        });
        setModalVisible(true);
        // Company fields are set via useEffect after the form fields are rendered
    };

    const handleSelectClient = (client: ClientData) => {
        setSelectedClient(client);
    };

    const handleIdCardRead = (
        data: IdCardReadResult & {
            photoBase64?: string;
            installationId?: string | null;
            /** What the service reported about the card photo, for the audit the save will write. */
            photo?: { state: string; code: string } | null;
        },
    ) => {
        if (!data || !form) {
            return;
        }

        // The card also supplies the ID series/number, issuer and validity dates;
        // these were previously dropped and left blank after a read.
        form.setFieldsValue({
            ...mapIdCardToClientForm(data),
            telefon: '',
            email: '',
        });
        // The DG2 portrait can only be stored once the client has an id, so hold it
        // until the client has been saved.
        // An empty portrait is not a portrait. The read path drops bytes whose status refuses them
        // and leaves the empty string behind, and a card read with no CAN never had any - both
        // arrive here the same way, and neither may become an upload. Testing for `undefined` alone
        // let both through to a PUT carrying nothing, which would have recorded a stored portrait
        // that does not exist.
        const portrait = data.photoBase64?.trim() ?? '';
        setPendingPortrait(portrait === '' ? null : { source: 'chip', base64: portrait });
        // The photo outcome is carried through exactly as the service reported it. A read from an
        // older service reports nothing, which is recorded as "not reported" rather than guessed at.
        setScanProvenance(chipProvenance(data.installationId ?? null, data.photo ?? null));
    };

    /**
     * Applies what an optical scan read.
     *
     * Deliberately not `handleIdCardRead` with a different source. The chip supplies a whole person
     * and replacing the form is right; a scan supplies five fields from a machine-readable zone and
     * has no opinion about the rest, so this merges rather than replaces and never blanks a field
     * the operator typed. The mapper has already dropped anything the document's own check digits
     * refused, and anything the operator declined.
     */
    const handleOpticalScanApplied = (
        values: Partial<ClientFormValues>,
        portrait: Blob | null,
        provenance: ScanProvenance | null,
    ) => {
        if (!form) {
            return;
        }

        form.setFieldsValue(values);
        setScanProvenance(provenance);

        if (portrait !== null) {
            // Same holding pattern as the chip portrait: it can only be stored once the client has
            // an id, so it waits until the client has been saved. Only the portrait is taken - the
            // visible and infrared images stay on the workstation and are never offered here.
            setPendingPortrait({ source: 'optical', blob: portrait });
        }
    };

    /**
     * Records a read that ended without producing anything.
     *
     * Deliberately fire-and-forget and deliberately silent on failure: this is an audit of a scan
     * that already failed, and telling an operator that the record of their failure could not be
     * written would be the second unhelpful message in a row. The body carries only allowlisted
     * metadata, built by the same function the save uses.
     */
    const reportScanOutcome = (
        eventType: 'scan_completed' | 'scan_failed' | 'scan_cancelled',
        provenance: ScanProvenance | null,
        outcomeCode: string | null,
    ) => {
        if (provenance === null) {
            return;
        }

        const fields = buildScanAuditFields(provenance);

        void request({
            path: '/clients/document-scan-events',
            method: 'POST',
            params: {
                eventType,
                ...fields,
                document_scan: {
                    ...fields.document_scan,
                    ...(outcomeCode === null ? {} : { outcomeCode }),
                },
            },
        }).catch((error: unknown) => {
            console.error('Document scan outcome not recorded:', describeFailure(error));
        });
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            // Map form field names to backend API field names
            const mappedValues = {
                client_name: values.nume,
                phone: values.telefon,
                mobile_number: values.mobil,
                email: values.email,
                personal_code: isCompany ? null : values.cnp,
                city: values.oras,
                street: values.str,
                street_number: values.nr,
                apartment: values.ap,
                county: values.jud,
                postal_code: values.codPostal,
                id_card_series: values.serieCI,
                id_card_number: values.nrCI,
                id_issued_by: values.emitentCI,
                id_card_issue_date: values.dataEliberareCI,
                id_card_expiry_date: values.dataExpirareCI,
                notes: values.notite,
                tax_code: isCompany ? values.taxCode : null,
                registration_number: isCompany ? values.registrationNumber : null,
                delegate: isCompany ? values.delegate : null,
            };

            // Sanitize: convert empty strings to null
            const sanitized = Object.fromEntries(
                Object.entries(mappedValues).map(([k, v]) => [k, v === '' ? null : v]),
            );

            // The payload is never logged. It is the client's name, personal numeric code,
            // identity document number and domicile, and a console is a place screenshots come
            // from. The field names alone are enough to see what a save carried.
            console.log('Submitting client form fields:', Object.keys(sanitized).join(', '));

            await saveClient(sanitized);
        } catch (error) {
            // Not the error object: antd's validation failure carries the form's values.
            console.error('Client form validation failed:', describeFailure(error));
            setAlertMessage({
                type: 'error',
                text: t('clients.validationFailed'),
            });
        }
    };

    /**
     * Stores a portrait read from an ID card against the saved client. A failure
     * here must not present the client save itself as failed — the client record
     * is already persisted — so it surfaces as a warning instead.
     */
    const uploadPendingPhoto = async (clientId?: number) => {
        if (pendingPortrait === null || !clientId) {
            return;
        }

        // The one place a portrait becomes base64, and the only image that ever does. `encoded` is
        // a local: it goes into one request and is unreachable the moment this function returns.
        const encoded =
            pendingPortrait.source === 'chip'
                ? pendingPortrait.base64
                : await toBase64(pendingPortrait.blob);

        if (encoded === null) {
            setAlertMessage({ type: 'warning', text: t('clients.photoUploadFailed') });
            setPendingPortrait(null);
            return;
        }

        try {
            await request({
                path: `/clients/${clientId}/photo`,
                method: 'PUT',
                params: {
                    photoBase64: encoded,
                    ...(scanProvenance === null ? {} : buildScanAuditFields(scanProvenance)),
                },
            });
        } catch (error) {
            // Not the error object: a rejected upload carries the portrait it was sending.
            console.error('Client photo upload failed:', describeFailure(error));
            setAlertMessage({ type: 'warning', text: t('clients.photoUploadFailed') });
        } finally {
            setPendingPortrait(null);
        }
    };

    const saveClient = async (sanitized: Record<string, unknown>) => {
        const isUpdate = !!editingClient;
        const path = isUpdate ? `/clients/${editingClient.ClNr}` : '/clients';
        const method = isUpdate ? 'PUT' : 'POST';
        const successKey = isUpdate ? 'clients.updateSuccess' : 'clients.createSuccess';
        const failKey = isUpdate ? 'clients.updateFailed' : 'clients.createFailed';

        // The client id identifies the row; `sanitized` is the person, and used to be logged in
        // full on every save.
        console.log(
            isUpdate ? 'Updating client:' : 'Creating new client:',
            isUpdate ? editingClient.ClNr : '',
        );

        try {
            const response = await request<{ client?: { ClNr?: number } }>({
                path,
                method,
                params: {
                    ...sanitized,
                    // Only when the values really came off a document. The backend stamps
                    // `scannedDate` and writes the audit row from this, after the save succeeds.
                    ...(scanProvenance === null ? {} : buildScanAuditFields(scanProvenance)),
                },
            });
            // A new client only gets its id from the create response.
            const savedClientId = isUpdate ? editingClient.ClNr : response?.client?.ClNr;
            await uploadPendingPhoto(savedClientId);
            setAlertMessage({ type: 'success', text: t(successKey) });
            setModalVisible(false);
            reloadClients();
        } catch (error) {
            // Not the error object: a rejected save carries the client payload it was sending.
            console.error(`${method} failed:`, describeFailure(error));
            const detail = error instanceof Error ? error.message : '';
            const failMessage = detail ? t(failKey) + ': ' + detail : t(failKey);
            setAlertMessage({ type: 'error', text: failMessage });
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        form.resetFields();
        setEditingClient(null);
        setIsCompany(false);
        // Drop the card photo too, so it cannot attach to the next client edited.
        setPendingPortrait(null);
        setScanProvenance(null);
    };

    const columns = [
        {
            key: 'ClNr',
            title: 'ID',
            dataIndex: 'ClNr',
            sortable: true,
            filterable: true,
            width: 80,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Nume',
            title: t('clients.lastName'),
            dataIndex: 'Nume',
            sortable: true,
            filterable: true,
            width: 200,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Telefon',
            title: t('clients.phone'),
            dataIndex: 'Telefon',
            sortable: false,
            filterable: true,
            width: 150,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Email',
            title: t('clients.email'),
            dataIndex: 'Email',
            sortable: false,
            filterable: true,
            width: 200,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Oras',
            title: t('clients.city'),
            dataIndex: 'Oras',
            sortable: true,
            filterable: true,
            width: 150,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'CNP',
            title: t('clients.personalCode'),
            dataIndex: 'CNP',
            sortable: true,
            filterable: true,
            width: 140,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'SerieCI',
            title: t('clients.idCardSeries'),
            dataIndex: 'SerieCI',
            sortable: false,
            filterable: true,
            width: 80,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'NrCI',
            title: t('clients.idCardNumber'),
            dataIndex: 'NrCI',
            sortable: false,
            filterable: true,
            width: 100,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
    ];

    const initialFilters = useMemo(() => {
        const clientId = searchParams.get('clientId');
        const name = searchParams.get('name');
        if (clientId ?? name) {
            const filters: Record<string, string> = {};
            if (clientId) filters.ClNr = clientId;
            if (name) filters.Nume = name;
            return filters;
        }
        return undefined;
    }, [searchParams]);

    // Auto-select client from URL params when data loads
    const autoSelectedRef = useRef(false);
    useEffect(() => {
        const clientId = searchParams.get('clientId');
        if (clientId && clients.length > 0 && !autoSelectedRef.current) {
            const client = clients.find(c => c.ClNr === Number(clientId));
            if (client) {
                autoSelectedRef.current = true;
                setSelectedClient(client);
            }
        }
    }, [clients, searchParams]);

    return (
        <Box>
            {alertMessage && (
                <Alert
                    severity={alertMessage.type}
                    onClose={() => setAlertMessage(null)}
                    sx={{ mb: 2 }}
                >
                    {alertMessage.text}
                </Alert>
            )}
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#1677ff' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <TeamOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('clients.title')}
                        </Title>
                        <Text type="secondary">{t('clients.subtitle')}</Text>
                    </div>
                </div>
                <div className="page-header__actions">
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddClient}>
                        {t('clients.add')}
                    </Button>
                </div>
            </div>

            <div style={{ display: 'flex' }}>
                <div style={{ flex: 'auto' }}>
                    <WebGrid<ClientData>
                        data={clients}
                        columns={columns}
                        loading={loading}
                        pagination={true}
                        pageSize={20}
                        filterBar={true}
                        initialFilters={initialFilters}
                        exportable={isAdmin}
                        selectable={true}
                        multiSelect={false}
                        showCheckboxes={false}
                        rowIdAccessor={row => row.ClNr}
                        selectedRows={selectedClient ? [selectedClient] : []}
                        onSelectionChange={rows => setSelectedClient(rows[0] ?? null)}
                        onRowDoubleClick={handleEditClient}
                        onRowClick={handleSelectClient}
                        onExport={() => {}}
                        height="600px"
                        emptyMessage={t('clients.noData')}
                    />
                </div>
                <div
                    style={{
                        width: '150px',
                        height: '170px',
                        flexShrink: 0,
                        border: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa',
                        marginLeft: 16,
                    }}
                >
                    {selectedClient && selectedClientPhotoUrl && !photoFailed ? (
                        <img
                            key={selectedClientPhotoUrl}
                            src={selectedClientPhotoUrl}
                            alt={selectedClient.Nume}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <span style={{ color: '#999', textAlign: 'center' }}>No Photo</span>
                    )}
                </div>
            </div>

            <Modal
                title={editingClient ? t('clients.edit') : t('clients.add')}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={800}
                okText={editingClient ? t('clients.update') : t('clients.create')}
            >
                <div style={{ marginBottom: 16 }}>
                    <IdentityReaderLauncher
                        active={modalVisible}
                        onChip={() => setIdCardModalVisible(true)}
                        onOptical={(deviceId, installationId) => {
                            setOpticalInstallationId(installationId);
                            setScanningDeviceId(deviceId);
                        }}
                    />
                </div>
                <Form form={form} layout="vertical" name="clientForm">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="nume"
                                label={t('clients.name')}
                                rules={[{ required: true, message: t('clients.nameRequired') }]}
                            >
                                <Input placeholder={t('clients.namePlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="cnp"
                                label={t('clients.personalCode')}
                                rules={
                                    isCompany
                                        ? []
                                        : [
                                              {
                                                  required: true,
                                                  message: t('clients.cnpRequired'),
                                              },
                                              {
                                                  min: 3,
                                                  message: t('clients.cnpMinLength'),
                                              },
                                          ]
                                }
                            >
                                <Input placeholder={t('clients.cnpPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="serieCI" label={t('clients.idCardSeries')}>
                                <Input
                                    placeholder={t('clients.idCardSeriesPlaceholder')}
                                    maxLength={10}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="nrCI" label={t('clients.idCardNumber')}>
                                <Input
                                    placeholder={t('clients.idCardNumberPlaceholder')}
                                    maxLength={20}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="emitentCI" label={t('clients.idIssuedBy')}>
                                <Input placeholder={t('clients.idIssuedByPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="dataEliberareCI" label={t('clients.idIssueDate')}>
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="dataExpirareCI" label={t('clients.idExpiryDate')}>
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="telefon"
                                label={t('clients.phone')}
                                rules={[
                                    {
                                        pattern: /^[0-9+\-\s()]+$/,
                                        message: t('clients.phoneInvalid'),
                                    },
                                ]}
                            >
                                <Input placeholder={t('clients.phonePlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="mobil"
                                label={t('clients.mobile')}
                                rules={[
                                    {
                                        pattern: /^[0-9+\-\s()]+$/,
                                        message: t('clients.phoneInvalid'),
                                    },
                                ]}
                            >
                                <Input placeholder={t('clients.mobilePlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="email"
                                label={t('clients.email')}
                                rules={[{ type: 'email', message: t('clients.emailInvalid') }]}
                            >
                                <Input placeholder={t('clients.emailPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="jud" label={t('clients.county')}>
                                <Input placeholder={t('clients.countyPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="oras" label={t('clients.city')}>
                                <Input placeholder={t('clients.cityPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="codPostal" label={t('clients.postalCode')}>
                                <Input
                                    placeholder={t('clients.postalCodePlaceholder')}
                                    maxLength={10}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="str" label={t('clients.street')}>
                                <Input placeholder={t('clients.streetPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="nr" label={t('clients.streetNumber')}>
                                <Input placeholder={t('clients.streetNumberPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="ap" label={t('clients.apartment')}>
                                <Input placeholder={t('clients.apartmentPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="notite" label={t('clients.notes')}>
                                <Input.TextArea
                                    rows={3}
                                    placeholder={t('clients.notesPlaceholder')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={24}>
                            <Checkbox
                                checked={isCompany}
                                onChange={e => {
                                    setIsCompany(e.target.checked);
                                    // Re-validate CNP field when toggling client type
                                    setTimeout(
                                        () => form.validateFields(['cnp']).catch(() => {}),
                                        0,
                                    );
                                }}
                            >
                                {t('clients.isCompany')}
                            </Checkbox>
                        </Col>
                    </Row>

                    {isCompany && (
                        <>
                            <Row gutter={16} style={{ marginTop: 16 }}>
                                <Col span={12}>
                                    <Form.Item
                                        name="taxCode"
                                        label={t('clients.taxCode')}
                                        rules={[
                                            {
                                                required: true,
                                                message: t('clients.taxCodeRequired'),
                                            },
                                        ]}
                                    >
                                        <Input placeholder={t('clients.taxCodePlaceholder')} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="registrationNumber"
                                        label={t('clients.registrationNumber')}
                                    >
                                        <Input
                                            placeholder={t('clients.registrationNumberPlaceholder')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="delegate"
                                        label={t('clients.delegate')}
                                        rules={[
                                            {
                                                required: true,
                                                message: t('clients.delegateRequired'),
                                            },
                                        ]}
                                    >
                                        <Input placeholder={t('clients.delegatePlaceholder')} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Modal>

            <IdCardReaderModal
                visible={idCardModalVisible}
                onClose={() => setIdCardModalVisible(false)}
                onDataRead={handleIdCardRead}
            />

            <DocumentScanModal
                visible={scanningDeviceId !== null}
                deviceId={scanningDeviceId}
                installationId={opticalInstallationId}
                onTerminalOutcome={reportScanOutcome}
                currentValues={form?.getFieldsValue() ?? {}}
                onClose={() => setScanningDeviceId(null)}
                onApply={handleOpticalScanApplied}
            />
        </Box>
    );
};

export default Clients;
